// ControlsPanel.tsx
// Left-pane control panel — replaces the header action buttons and the
// top graph-controls bar.  Organised into role-scoped sections.

import React, { useCallback, useMemo, useRef, useState } from 'react';
import SearchBar from './SearchBar';
import AskDeweyPanel from './AskDeweyPanel';
import AskDeweyAnswer from './AskDeweyAnswer';
import ReviewPanel from './ReviewPanel';
import TypeManager from './TypeManager';
import { adminApi, agentApi, curationApi, mcpKeyApi } from '../services/api';
import { MOCK_ASK_DEWEY_RESPONSE } from '../services/askDeweyMock';
import { getNodeBaseColor } from '../utils/colors';
import type { AnswerEntityIndex } from '../utils/askDeweyEntities';
import type { GraphSearchResponse, RelCreationState, SearchResult, ProposalSubmitEntry, NodeType, NodeInstance } from '../types';
import './ControlsPanel.css';

type ControlsSectionKey = 'navigate' | 'details' | 'edit' | 'admin';

const IS_DEV_BUILD = process.env.NODE_ENV !== 'production';

export interface ControlsPanelProps {
  // ── Viewer ──────────────────────────────────────────────────────────────
  layout: 'force' | 'icicle' | '3d' | 'content';
  onSearchResults: (results: SearchResult[]) => void;
  includePendingSearch: boolean;
  searchSessionId?: string | null;
  searchResultCount: number;
  searchResults: SearchResult[];
  onFocusSearchResult: (result: SearchResult) => void;
  focusNodeId?: string | null;
  /** Nodes currently loaded in the graph; grounds Ask Dewey entity resolution. */
  graphNodes?: readonly NodeInstance[];
  /** Focuses a node referenced by an Ask Dewey answer. */
  onFocusNode?: (node: NodeInstance) => void;
  /** Reports node ids referenced by the current answer, so they can be highlighted. */
  onAskDeweyEntitiesChange?: (nodeIds: string[]) => void;
  /** Clears Ask Dewey focus and node detail state held by Layout. */
  onClearAskDewey?: () => void;
  nodeTypes: NodeType[];
  hiddenActive: boolean;
  onRestoreHidden: () => void;
  schemaMode: boolean;
  onSchemaModeChange: (enabled: boolean) => void;

  // ── Status ──────────────────────────────────────────────────────────────
  errorMessage: string | null;
  onDismissError: () => void;

  // ── Relationship-creation feedback ──────────────────────────────────────
  relCreation: RelCreationState;
  onCancelRelCreation: () => void;

  // ── Proposer ────────────────────────────────────────────────────────────

  onSaveSession: () => void;
  onCancelSession: () => void;

  // ── Review ──────────────────────────────────────────────────────────────
  onProposalActioned?: () => void;
  canApprove?: boolean;
  localQueue?: readonly ProposalSubmitEntry[];
  onPreviewSession?: (proposals: ProposalSubmitEntry[], selectedIndices: number[] | null) => void;
  onPreviewLocalIndex?: (queue: readonly ProposalSubmitEntry[], index: number | null) => void;
  onSelectedSessionChange?: (sessionId: string | null) => void;
  onViewSource?: (indexName: string, documentId: string, chunk: string | null) => void;
  sessionRefreshKey?: number;
  /** Externally request a session to be selected in the ReviewPanel. */
  requestedSessionId?: string | null;

  // ── Session tree actions ────────────────────────────────────────────────
  onResume?: (sessionId: string) => void;
  onCancelSessionById?: (sessionId: string) => Promise<void>;
  onModify?: (parentSessionId: string) => void;
  onApprove?: (sessionId: string) => void;
  /** ID of the currently active edit session. */
  activeSessionId?: string;

  // ── Curator ─────────────────────────────────────────────────────────────
  isAdmin: boolean;
  isCurator: boolean;

  // ── Role badges ─────────────────────────────────────────────────────────
  /** Permissions are displayed in the header by Layout; retained here for future use. */
  permissions: string[];
  onSchemaRulesUpdated?: () => void;
  onSummaryBackfillSubmitted?: () => void;
  children?: React.ReactNode;
}

const ControlsPanel: React.FC<ControlsPanelProps> = ({
  onSearchResults,
  includePendingSearch,
  searchSessionId,
  searchResultCount,
  searchResults,
  onFocusSearchResult,
  focusNodeId,
  graphNodes,
  onFocusNode,
  onAskDeweyEntitiesChange,
  onClearAskDewey,
  nodeTypes,
  hiddenActive,
  onRestoreHidden,
  onSchemaModeChange,
  errorMessage,
  onDismissError,
  relCreation,
  onCancelRelCreation,
  onSaveSession,
  onCancelSession,
  onProposalActioned,
  canApprove = false,
  localQueue = [],
  onPreviewSession,
  onPreviewLocalIndex,
  onSelectedSessionChange,
  onViewSource,
  sessionRefreshKey = 0,
  requestedSessionId,
  onResume,
  onCancelSessionById,
  onModify,
  onApprove,
  activeSessionId,
  isAdmin,
  isCurator,
  onSchemaRulesUpdated,
  onSummaryBackfillSubmitted,
  children,
}) => {
  const [backfillStatus, setBackfillStatus] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [backfillMessage, setBackfillMessage] = useState<string | null>(null);
  const [summaryBackfillStatus, setSummaryBackfillStatus] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [summaryBackfillMessage, setSummaryBackfillMessage] = useState<string | null>(null);
  const [deleteRejectedStatus, setDeleteRejectedStatus] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [deleteRejectedMessage, setDeleteRejectedMessage] = useState<string | null>(null);
  const [resetPipelineStatus, setResetPipelineStatus] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [resetPipelineMessage, setResetPipelineMessage] = useState<string | null>(null);
  const [mcpKeyCopied, setMCPKeyCopied] = useState<boolean>(false);
  const [showSchemaEditor, setShowSchemaEditor] = useState<boolean>(false);
  const [authFlash, setAuthFlash] = useState<string | null>(null);
  const [navigateMode, setNavigateMode] = useState<'search' | 'ask-dewey'>('search');
  const [askDeweyLoading, setAskDeweyLoading] = useState<boolean>(false);
  const [askDeweyResult, setAskDeweyResult] = useState<GraphSearchResponse | null>(null);
  const [askDeweyError, setAskDeweyError] = useState<string | null>(null);
  const [askDeweyUseMock, setAskDeweyUseMock] = useState<boolean>(false);
  const [collapsedSections, setCollapsedSections] = useState<Record<ControlsSectionKey, boolean>>({
    navigate: false,
    details: false,
    edit: false,
    admin: false,
  });
  const authFlashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const toggleSection = (section: ControlsSectionKey): void => {
    setCollapsedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const showNotAuthorized = (msg = 'Not Authorized'): void => {
    if (authFlashTimer.current) clearTimeout(authFlashTimer.current);
    setAuthFlash(msg);
    authFlashTimer.current = setTimeout(() => setAuthFlash(null), 2200);
  };

  /** Returns click handler: calls real action when authorized, fires flash otherwise. */
  const guarded = (authorized: boolean, action: () => void, msg?: string) =>
    () => authorized ? action() : showNotAuthorized(msg);

  const handleAskDeweySubmit = async (query: string): Promise<void> => {
    setAskDeweyLoading(true);
    setAskDeweyError(null);
    setAskDeweyResult(null);
    onAskDeweyEntitiesChange?.([]);
    try {
      if (IS_DEV_BUILD && askDeweyUseMock) {
        setAskDeweyResult(MOCK_ASK_DEWEY_RESPONSE);
        return;
      }
      const data = await agentApi.search(query, 10, searchSessionId);
      setAskDeweyResult(data);
    } catch (err) {
      setAskDeweyError((err as Error).message || 'Query failed.');
    } finally {
      setAskDeweyLoading(false);
    }
  };

  const handleAskDeweyClear = (): void => {
    setAskDeweyResult(null);
    setAskDeweyError(null);
    onAskDeweyEntitiesChange?.([]);
    onClearAskDewey?.();
  };

  const handleAskDeweyMockToggle = (enabled: boolean): void => {
    if (!IS_DEV_BUILD) return;
    setAskDeweyUseMock(enabled);
    setAskDeweyError(null);
    setAskDeweyResult(enabled ? MOCK_ASK_DEWEY_RESPONSE : null);
    if (!enabled) onAskDeweyEntitiesChange?.([]);
  };

  /** Lifts the answer's grounded entities so the graph can highlight them. */
  const handleAskDeweyEntitiesResolved = useCallback(
    (index: AnswerEntityIndex): void => {
      onAskDeweyEntitiesChange?.(index.entities.map((e) => e.node.id));
    },
    [onAskDeweyEntitiesChange],
  );

  const handleCopyMCPKey = async (): Promise<void> => {
    try {
      const { token } = await mcpKeyApi.getKey();
      await navigator.clipboard.writeText(token);
      setMCPKeyCopied(true);
      setTimeout(() => setMCPKeyCopied(false), 2000);
    } catch {
      showNotAuthorized('Failed to copy MCP key');
    }
  };

  const handleBackfill = async (): Promise<void> => {
    setBackfillStatus('running');
    setBackfillMessage(null);
    try {
      const result = await adminApi.backfillEmbeddings();
      setBackfillStatus('done');
      setBackfillMessage(
        result.backfilled === 0
          ? 'All nodes already have embeddings.'
          : `Backfilled ${result.backfilled} node${result.backfilled === 1 ? '' : 's'}.`
      );
    } catch (err) {
      setBackfillStatus('error');
      setBackfillMessage((err as Error).message || 'Backfill failed.');
    }
  };

  const handleDeleteRejected = async (): Promise<void> => {
    setDeleteRejectedStatus('running');
    setDeleteRejectedMessage(null);
    try {
      const result = await adminApi.deleteRejectedSessions();
      setDeleteRejectedStatus('done');
      setDeleteRejectedMessage(
        result.deleted === 0
          ? 'No non-open sessions found.'
          : `Deleted ${result.deleted} non-open session${result.deleted === 1 ? '' : 's'}.`
      );
    } catch (err) {
      setDeleteRejectedStatus('error');
      setDeleteRejectedMessage((err as Error).message || 'Delete failed.');
    }
  };

  const handleSummaryBackfill = async (): Promise<void> => {
    setSummaryBackfillStatus('running');
    setSummaryBackfillMessage(null);
    try {
      const result = await curationApi.backfillSummaries({});
      setSummaryBackfillStatus('done');
      setSummaryBackfillMessage(
        result.processed === 0
          ? 'No nodes required summary backfill.'
          : `Submitted ${result.processed} summary backfill session${result.processed === 1 ? '' : 's'}.`
      );
      onSummaryBackfillSubmitted?.();
    } catch (err) {
      setSummaryBackfillStatus('error');
      setSummaryBackfillMessage((err as Error).message || 'Summary backfill failed.');
    }
  };

  const handleResetPipeline = async (): Promise<void> => {
    const confirmed = window.confirm(
      'Reset user-document testing pipeline? This will remove non-open sessions (except live), reset source-user chunk status, and clear rejections/justifications indexes.'
    );
    if (!confirmed) {
      return;
    }

    setResetPipelineStatus('running');
    setResetPipelineMessage(null);
    try {
      const result = await adminApi.resetUserPipeline();
      setResetPipelineStatus('done');
      setResetPipelineMessage(
        `Reset complete. Sessions deleted: ${result.neo4j_sessions_deleted}. Chunks reset: ${result.source_user_chunks_reset}. Rejections deleted: ${result.rejections_deleted}. Justifications deleted: ${result.justifications_deleted}.`
      );
    } catch (err) {
      setResetPipelineStatus('error');
      setResetPipelineMessage((err as Error).message || 'Reset failed.');
    }
  };

  const getReadableTextColor = (hexColor: string): string => {
    const raw = (hexColor || '').trim();
    if (!/^#[0-9A-Fa-f]{6}$/.test(raw)) {
      return '#1f2933';
    }
    const r = parseInt(raw.slice(1, 3), 16);
    const g = parseInt(raw.slice(3, 5), 16);
    const b = parseInt(raw.slice(5, 7), 16);
    const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    return luminance > 0.58 ? '#1f2933' : '#ffffff';
  };

  const nodeTypeColors = useMemo(
    () => new Map(nodeTypes.map((typeDef) => [typeDef.type.toLowerCase(), getNodeBaseColor(typeDef.color)])),
    [nodeTypes]
  );

  const handleToggleSchemaEditor = (): void => {
    setShowSchemaEditor((prev) => {
      const next = !prev;
      onSchemaModeChange(next);
      return next;
    });
  };

  return (
    <div className="controls-panel">

      {/* ── Status / error banner ─────────────────────────────────────── */}
      {errorMessage && (
        <div className="status-message status-message--error controls-panel__status">
          {errorMessage}
          <button
            className="status-message-dismiss"
            onClick={onDismissError}
            aria-label="Dismiss"
          >
            <i className="modus-icons" aria-hidden="true">close</i>
          </button>
        </div>
      )}

      {/* ── Relationship-creation feedback ────────────────────────────── */}
      {relCreation.active && (
        <div className="controls-panel__rel-status">
          <span>
            Creating <b>{relCreation.relationshipName ?? relCreation.relType?.name}</b> from <b>{relCreation.sourceNode?.name}</b>:
          </span>
          {relCreation.error ? (
            <span className="rel-creation-error"> {relCreation.error}</span>
          ) : relCreation.allowedTargets.length === 0 ? (
            <span> No allowed targets.</span>
          ) : (
            <span> Select a highlighted target node.</span>
          )}
          <button className="btn-cancel controls-btn" onClick={onCancelRelCreation}>
            Cancel
          </button>
        </div>
      )}

      {/* ── Viewer section — always visible ──────────────────────────── */}
      <section className="controls-section controls-section--viewer">
        <h4 className="controls-section__title controls-section__title--toggle" onClick={() => toggleSection('navigate')}>Navigate</h4>

        {!collapsedSections.navigate && (
          <>

            {/* ── Search/Ask Dewey toggle ────────────────────────────────────── */}
            <div className="controls-row controls-row--navigate-mode">
              <div className="controls-mode-toggle">
                <button
                  className={`controls-mode-btn ${navigateMode === 'search' ? 'controls-mode-btn--active' : ''}`}
                  onClick={() => setNavigateMode('search')}
                  type="button"
                  aria-pressed={navigateMode === 'search'}
                >
                  Search
                </button>
                <button
                  className={`controls-mode-btn ${navigateMode === 'ask-dewey' ? 'controls-mode-btn--active' : ''}`}
                  onClick={() => setNavigateMode('ask-dewey')}
                  type="button"
                  aria-pressed={navigateMode === 'ask-dewey'}
                >
                  Ask Dewey
                </button>
              </div>
            </div>

            {/* ── Search mode ────────────────────────────────────────────────────── */}
            {navigateMode === 'search' && (
              <>
                <div className="controls-row controls-row--search">
                  <SearchBar
                    onResults={onSearchResults}
                    includePending={includePendingSearch}
                    sessionId={searchSessionId}
                    resultCount={searchResultCount}
                  />
                </div>

                {searchResults.length > 0 && (
                  <div className="controls-row controls-row--search-results">
            <div className="controls-search-results" role="list" aria-label="Search results">
                      {searchResults.map((result) => {
                        const typeColor = nodeTypeColors.get(result.node.type.toLowerCase()) ?? getNodeBaseColor(result.node.color);
                        const description = result.node.description?.trim() || 'No description available.';
                        const scorePercent = Math.round(result.score * 100);
                        const isFocusedResult = focusNodeId === result.node.id;
                        return (
                          <button
                            key={result.node.id}
                            type="button"
                            className={`controls-search-result${isFocusedResult ? ' controls-search-result--focused' : ''}`}
                            onClick={() => onFocusSearchResult(result)}
                            role="listitem"
                            title={`Focus ${result.node.name}`}
                            aria-label={`Focus ${result.node.name}`}
                            aria-pressed={isFocusedResult}
                          >
                            <span className="controls-search-result__header">
                              <span
                                className="controls-search-result__name"
                                style={{ backgroundColor: typeColor, color: getReadableTextColor(typeColor) }}
                                title={description}
                              >
                                {result.node.name}
                              </span>
                              <span
                                className="controls-search-result__score"
                                title="Search similarity score. Higher percentages indicate a closer match to the query."
                                aria-label={`Search similarity score ${scorePercent} percent. Higher percentages indicate a closer match to the query.`}
                              >
                                {scorePercent}%
                              </span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ── Ask Dewey mode ─────────────────────────────────────────────────── */}
            {navigateMode === 'ask-dewey' && (
              <div className="controls-row controls-row--ask-dewey">
            <AskDeweyPanel
              onSubmit={handleAskDeweySubmit}
              onClear={handleAskDeweyClear}
              hasClearableState={askDeweyResult !== null || askDeweyError !== null}
              isLoading={askDeweyLoading}
            />
                {IS_DEV_BUILD && (
                  <label className="ask-dewey-mock-toggle">
                    <input
                      type="checkbox"
                      checked={askDeweyUseMock}
                      onChange={(e) => handleAskDeweyMockToggle(e.target.checked)}
                    />
                    <span>Use mock response (dev)</span>
                  </label>
                )}
                {askDeweyError && (
                  <div className="ask-dewey-error" role="alert">
                    {askDeweyError}
                  </div>
                )}
                {askDeweyResult && (
                  <div className="ask-dewey-result">
                    {askDeweyResult.answer ? (
                      <AskDeweyAnswer
                        answer={askDeweyResult.answer}
                        graphNodes={graphNodes}
                        results={askDeweyResult.results}
                        onFocusNode={onFocusNode}
                        focusNodeId={focusNodeId}
                        onEntitiesResolved={handleAskDeweyEntitiesResolved}
                      />
                    ) : (
                      <div className="ask-dewey-result__answer">No answer found.</div>
                    )}
                    {askDeweyResult.clarification_needed && askDeweyResult.clarification_question && (
                        <div className="ask-dewey-result__note">
                          <strong>Note:</strong> {askDeweyResult.clarification_question}
                        </div>
                      )}
                  </div>
                )}
              </div>
            )}

            {hiddenActive && (
              <div className="controls-row">
                <button className="btn-secondary controls-btn" onClick={onRestoreHidden}>
                  Show All Hidden
                </button>
              </div>
            )}

          </>
        )}
      </section>

      {/* ── Node detail — passed as children ────────────────────────── */}
      {children && (
        <section className="controls-section controls-section--detail">
          <h4 className="controls-section__title controls-section__title--toggle" onClick={() => toggleSection('details')}>Node Details</h4>
          {!collapsedSections.details && children}
        </section>
      )}

      {/* ── Edit section — always visible; restricted actions show flash ── */}
      <section className="controls-section controls-section--proposer">
        <h4 className="controls-section__title controls-section__title--toggle" onClick={() => toggleSection('edit')}>Edit</h4>

        {!collapsedSections.edit && (
          <>

            {authFlash && (
              <div className="controls-panel__auth-flash">
            <i className="modus-icons" aria-hidden="true">lock</i> {authFlash}
              </div>
            )}

            <ReviewPanel
              onProposalActioned={onProposalActioned}
              canApprove={canApprove}
              localQueue={localQueue}
              onPreviewSession={onPreviewSession}
              onPreviewLocalIndex={onPreviewLocalIndex}
              onSelectedSessionChange={onSelectedSessionChange}
              onViewSource={onViewSource}
              onResume={onResume}
              onCancelSession={onCancelSessionById}
              onModify={onModify}
              onApprove={onApprove}
              activeSessionId={activeSessionId}
              sessionRefreshKey={sessionRefreshKey}
              onSaveSession={onSaveSession}
              onCancelActiveSession={onCancelSession}
              requestedSessionId={requestedSessionId}
            />
          </>
        )}
      </section>

      {/* ── Curator / admin section — always visible ── */}
      <section className="controls-section controls-section--curator">
        <h4 className="controls-section__title controls-section__title--toggle" onClick={() => toggleSection('admin')}>Admin</h4>

        {!collapsedSections.admin && (
          <>

            {!isAdmin && (
              <p className="admin-panel__msg admin-panel__msg--idle">
                Schema mutation actions require the admin capability.
                {!isCurator ? ' Review approvals remain curator-gated.' : ''}
              </p>
            )}

            <div className="controls-row controls-row--admin-icons">
              <button
                className={`controls-icon-btn${mcpKeyCopied ? ' controls-icon-btn--active' : ''}`}
                onClick={handleCopyMCPKey}
                title={mcpKeyCopied ? 'Copied!' : 'Copy MCP API Key'}
                aria-label="Copy MCP API Key"
              >
            <i className="modus-icons" aria-hidden="true">{mcpKeyCopied ? 'check' : 'key'}</i>
              </button>
              <button
                className={`controls-icon-btn${!isAdmin ? ' controls-btn--unauthorized' : ''}`}
                aria-disabled={!isAdmin || backfillStatus === 'running'}
                onClick={guarded(isAdmin, handleBackfill, 'Requires Admin role')}
                title={isAdmin ? 'Backfill Embeddings' : 'Not Authorized'}
                aria-label="Backfill Embeddings"
              >
            <i className="modus-icons" aria-hidden="true">ai_stars</i>
              </button>
              <button
                className={`controls-icon-btn${!isAdmin ? ' controls-btn--unauthorized' : ''}`}
                aria-disabled={!isAdmin || summaryBackfillStatus === 'running'}
                onClick={guarded(isAdmin, handleSummaryBackfill, 'Requires Admin role')}
                title={isAdmin ? 'Backfill Semantic Summaries' : 'Not Authorized'}
                aria-label="Backfill Semantic Summaries"
              >
            <i className="modus-icons" aria-hidden="true">file_edit</i>
              </button>
              <button
                className={`controls-icon-btn${!isAdmin ? ' controls-btn--unauthorized' : ''}`}
                aria-disabled={!isAdmin || deleteRejectedStatus === 'running'}
                onClick={guarded(isAdmin, handleDeleteRejected, 'Requires Admin role')}
                title={isAdmin ? 'clean up rejected and accepted sessions' : 'Not Authorized'}
                aria-label="Clean up rejected and accepted sessions"
              >
            <i className="modus-icons" aria-hidden="true">delete</i>
              </button>

              <button
                className={`controls-icon-btn${!isAdmin ? ' controls-btn--unauthorized' : ''}`}
                aria-disabled={!isAdmin || resetPipelineStatus === 'running'}
                onClick={guarded(isAdmin, handleResetPipeline, 'Requires Admin role')}
                title={isAdmin ? 'Reset user-document testing pipeline' : 'Not Authorized'}
                aria-label="Reset user-document testing pipeline"
              >
            <i className="modus-icons" aria-hidden="true">restart_alt</i>
              </button>

              <button
                className={`controls-icon-btn${!isAdmin ? ' controls-btn--unauthorized' : ''}${showSchemaEditor ? ' controls-icon-btn--active' : ''}`}
                aria-disabled={!isAdmin}
                onClick={guarded(isAdmin, handleToggleSchemaEditor, 'Requires Admin role')}
            title={isAdmin ? (showSchemaEditor ? 'Hide Schema Editor' : 'Edit Schema') : 'Not Authorized'}
                aria-label={showSchemaEditor ? 'Hide Schema Editor' : 'Edit Schema'}
              >
            <i className="modus-icons" aria-hidden="true">pencil</i>
              </button>
            </div>

            {backfillStatus === 'running' && (
          <p className="admin-panel__msg admin-panel__msg--idle">Running embeddings backfill...</p>
            )}

            {backfillMessage && (
              <p className={`admin-panel__msg admin-panel__msg--${backfillStatus}`}>
                {backfillMessage}
              </p>
            )}

            {summaryBackfillStatus === 'running' && (
          <p className="admin-panel__msg admin-panel__msg--idle">Submitting summary backfill sessions...</p>
            )}

            {summaryBackfillMessage && (
              <p className={`admin-panel__msg admin-panel__msg--${summaryBackfillStatus}`}>
                {summaryBackfillMessage}
              </p>
            )}

            {deleteRejectedStatus === 'running' && (
          <p className="admin-panel__msg admin-panel__msg--idle">Deleting non-open sessions...</p>
            )}

            {deleteRejectedMessage && (
              <p className={`admin-panel__msg admin-panel__msg--${deleteRejectedStatus}`}>
                {deleteRejectedMessage}
              </p>
            )}

            {resetPipelineStatus === 'running' && (
              <p className="admin-panel__msg admin-panel__msg--idle">Resetting user pipeline...</p>
            )}

            {resetPipelineMessage && (
              <p className={`admin-panel__msg admin-panel__msg--${resetPipelineStatus}`}>
                {resetPipelineMessage}
              </p>
            )}

            {isAdmin && showSchemaEditor && (
              <div className="controls-admin-schema-editor">
                <TypeManager isAdmin={isAdmin} onUpdated={onSchemaRulesUpdated} />
              </div>
            )}
          </>
        )}

      </section>


    </div>
  );
};

export default ControlsPanel;
